import * as React from 'react';
import { Autocomplete, Box, TextField, FormControl } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { SuccessFailureIndicator } from '../../../subs/SuccessFailureIndicator';
import { copyArrayWithIndexChanged } from '../../../Utils';
import { GridColDef, GridRowModel } from '@mui/x-data-grid';
import { WorkshopProduct, getWorkshopProducts, updateWorkshopProduct } from '../../../types/WorkshopProducts';
import { WorkshopProductStatusStrings, WorkshopProductStatus, ProductReleaseStatusStrings, ProductReleaseStatus } from '../../../types/WorkshopProducts';
import { Workshop, getWorkshops } from '../../../types/Workshop';
import { EditableTable } from '../../../subs/EditableTable';

import { AlertProps } from '@mui/material/Alert';
import { useSnackbar } from '../../../../contexts/SnackBarContext';

export const WorkshopProductsAdminView = () => {
    const { showSnackbar } = useSnackbar();
    const [workshops, setWorkshops] = React.useState<Workshop[]>([]);
    const [workshop, setWorkshop] = React.useState<Workshop>();
    const [allData, setallData] = React.useState<WorkshopProduct[]>([]);

    const showUserFeedback = React.useCallback((msg: string | null, severity: AlertProps['severity'] = 'error') => {
        msg && showSnackbar(msg, severity);
    }, [showSnackbar]);

    React.useEffect(() => {
        const fetchAllWorkshops = async () => {
            const allworkshopsResponse = await getWorkshops(new AbortController());
            if (allworkshopsResponse.failed) {
                showUserFeedback(allworkshopsResponse.errorText);
                return;
            }
            setWorkshops(allworkshopsResponse.result);
        }
        fetchAllWorkshops();
    }, [showUserFeedback]);

    React.useEffect(() => {
        const fetchAllWorkshopProducts = async () => {
            if (workshop === undefined || workshop.id === 0) return;
            const allWSProdsResponse = await getWorkshopProducts(workshop.id, new AbortController());
            if (allWSProdsResponse.failed) {
                showUserFeedback(allWSProdsResponse.errorText);
                return;
            }
            setallData(allWSProdsResponse.result);
        }
        fetchAllWorkshopProducts();
    }, [workshop, showUserFeedback]);

    const processRowUpdate = (newRow: GridRowModel) => {
        const changedData = allData.find(row => row.id === newRow.id);
        if (!changedData) {
            showUserFeedback('Row not found', 'error');
            return newRow;
        }
        changedData.status = newRow.status as WorkshopProductStatus;
        changedData.releaseStatus = newRow.releaseStatus as ProductReleaseStatus;
        changedData.clientReadyToTest = newRow.clientReadyToTest as boolean;
        changedData.serverReadyToTest = newRow.serverReadyToTest as boolean;
        saveHandler(changedData);
        return newRow;
    };

    const saveHandler = async (changedData: WorkshopProduct) => {
        const updateWSPRequest = await updateWorkshopProduct(changedData, new AbortController());

        if (updateWSPRequest.failed) {
            showUserFeedback(updateWSPRequest.errorText);
            return;
        }
        const updateWSP = updateWSPRequest.result;
        let allWSPCLoc = copyArrayWithIndexChanged(allData, allData.findIndex(tc => tc.id === updateWSP.id), updateWSP);
        setallData(allWSPCLoc);
    }

    const columns: GridColDef[] = [
        {
            field: 'guid',
            headerName: 'GUID',
            flex: 2
        },
        {
            field: 'workshopId',
            headerName: 'Workshop ID',
            flex: 1,
        },
        {
            field: 'productId',
            headerName: 'Product ID',
            flex: 1,
        },
        {
            field: 'productVersion',
            headerName: 'Product Version',
            flex: 1,
        },
        {
            field: 'description',
            headerName: 'Description',
            flex: 1,
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return WorkshopProductStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return WorkshopProductStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let status = 0;
                if (WorkshopProductStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < WorkshopProductStatusStrings.length; i++) {
                        if (WorkshopProductStatusStrings[i] === value) {
                            status = i;
                            break;
                        }
                    }
                }
                return { ...row, status: status };
            },
            type: 'singleSelect',
            valueOptions: WorkshopProductStatusStrings
        },
        {
            field: 'releaseStatus',
            headerName: 'Release Status',
            flex: 1,
            editable: true,
            valueFormatter: (value?: number) => {
                if (value == null) {
                    return '';
                }
                return ProductReleaseStatusStrings[value];
            },
            valueGetter: (value, row) => {
                if (value == null) {
                    return '';
                }
                return ProductReleaseStatusStrings[value];
            },
            valueSetter: (value, row) => {
                let releaseStatus = 0;
                if (ProductReleaseStatusStrings.find(v => v === value) !== null) {
                    for (let i = 0; i < ProductReleaseStatusStrings.length; i++) {
                        if (ProductReleaseStatusStrings[i] === value) {
                            releaseStatus = i;
                            break;
                        }
                    }
                }
                return { ...row, releaseStatus: releaseStatus };
            },
            type: 'singleSelect',
            valueOptions: ProductReleaseStatusStrings
        },
        {
            field: 'isClient',
            headerName: 'Is Client',
            type: 'boolean',
            flex: 1,
            editable: false,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.isClient} /> },
        },
        {
            field: 'isServer',
            headerName: 'Is Server',
            type: 'boolean',
            flex: 1,
            editable: false,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.isServer} /> },
        },
        {
            field: 'clientReadyToTest',
            headerName: 'Client Ready To Test',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.clientReadyToTest} /> },
        },
        {
            field: 'serverReadyToTest',
            headerName: 'Server Ready To Test',
            type: 'boolean',
            flex: 1,
            editable: true,
            renderCell: (params) => { return <SuccessFailureIndicator success={params.row.serverReadyToTest} /> },
        },
        {
            field: 'id'
        },
    ];

    return <React.Fragment >
        <Grid size={6} paddingBottom={10}>
            <FormControl required fullWidth>
                <Autocomplete id='ac-wsOptions' options={workshops.map(w => w.name)}
                    value={workshop ? workshop.name : ''}
                    onChange={(e, value) => setWorkshop(workshops.find(w => w.name === value))}
                    renderInput={(params) =>
                        <TextField name='wsOptions' multiline
                            {...params}
                            label={"Workshop"}
                            sx={{
                                '& .MuiOutlinedInput-root fieldset': {
                                    borderColor: 'divider',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused fieldset': {
                                    borderColor: 'divider',
                                },
                            }}
                        />
                    }
                />
            </FormControl>
        </Grid>
        {
            workshop &&
            <Box sx={{ flexGrow: 1, width: "100%" }} style={{ overflow: 'auto' }}>
                <EditableTable
                    columns={columns}
                    rows={allData}
                    setAllRows={setallData}
                    processRowUpdate={processRowUpdate}
                />
            </Box>
        }
    </React.Fragment >
}